import { React, useEffect, useState } from "react";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import ModalPopup from "../../components/ModalPopup";
import RedemptionForm from "./Forms/RedemptionForm";
import EntityCardDisplay from "../../components/EntityCardDisplay";
import { api } from "../../utils/api";
import RedemptionTable from "./RedemptionTable";
import RedemptionScanner from "./Widget/RedemptionScanner";
import { readApiErrorMessage } from "../../utils/apiErrorMessage";
import {
  PAID_DATE_FETCH_MAX_INVOICES,
  buildPaidDateFetchRequest,
  canFetchPaidDates
} from "../../utils/paidDateFetchAccess";

function currentUserEmail() {
  try {
    return JSON.parse(sessionStorage.getItem("currentUser") || "{}")?.email || null;
  } catch {
    return null;
  }
}

const Redemption = () => {
  const [redemptionItems, setRedemption] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRedemptionForm, setShowRedemptionForm] = useState(false);
  const [showRedemptionScanner, setShowRedemptionScanner] = useState(false);
  const [fetchingPaidDates, setFetchingPaidDates] = useState(false);
  const [paidDatesResult, setPaidDatesResult] = useState(null);
  const [paidDatesFrom, setPaidDatesFrom] = useState("");
  const [paidDatesLimit, setPaidDatesLimit] = useState(String(PAID_DATE_FETCH_MAX_INVOICES));
  const [showPaidDatesDialog, setShowPaidDatesDialog] = useState(false);
  const showFetchPaidDates = canFetchPaidDates(sessionStorage.getItem("selectedPortal"), currentUserEmail());

  const closePaidDatesDialog = () => {
    if (fetchingPaidDates) return;
    setShowPaidDatesDialog(false);
    setPaidDatesResult(null);
  };

  // Saves Xero paid dates for up to 50 paid bills that have none yet, oldest first from
  // the chosen date; dated bills are never re-fetched.
  const fetchPaidDates = async () => {
    const request = buildPaidDateFetchRequest(paidDatesFrom, paidDatesLimit);
    if (request.error) {
      setPaidDatesResult({ severity: "error", message: request.error });
      return;
    }
    setFetchingPaidDates(true);
    setPaidDatesResult(null);
    try {
      const response = await api("v1/xeroapi/fetch-redemption-paid-dates", "POST", request.body);
      const ok = Boolean(response?.data?.success);
      setPaidDatesResult({
        severity: ok ? "success" : "warning",
        message: response?.data?.message || readApiErrorMessage(response, "Could not fetch paid dates.")
      });
      if (response?.data?.data?.invoicesFilled > 0) setRefreshKey((prev) => prev + 1);
    } catch (error) {
      setPaidDatesResult({ severity: "error", message: readApiErrorMessage(error, "Could not fetch paid dates.") });
    } finally {
      setFetchingPaidDates(false);
    }
  };

  useEffect(() => {
    fetchRedemptions();
    const refreshForStore = () => fetchRedemptions();
    window.addEventListener("merchant-store-changed", refreshForStore);
    return () => window.removeEventListener("merchant-store-changed", refreshForStore);
  }, []);

  const fetchRedemptions = async () => {
    try {
      // For admin portal, let RedemptionTable handle its own data fetching with pagination
      if (sessionStorage.getItem("selectedPortal") == "Administrator") {
        setRedemption([]); // Empty array to trigger server-side pagination in RedemptionTable
        setLoading(false);
        return;
      }

      // Merchant portal: only the store selected for this session, not every site on the account.
      let endpoint = "";
      if (sessionStorage.getItem("selectedPortal") == "Merchant") {
        const selectedStore = sessionStorage.getItem("selectedMerchantStore");
        if (selectedStore) {
          endpoint = `v1/merchantStore/${selectedStore}/redemptions`;
        }
      }

      if (endpoint) {
        console.log("Fetching redemptions from:", endpoint);
        const response = await api(endpoint, "GET");
        if (response.data.success) {
          const redemptions = response.data.data.allRedemptions || [];
          console.log("Fetched redemptions:", redemptions.length);
          setRedemption(redemptions);
        } else {
          console.error("Failed to fetch redemptions:", response.data.message);
          setRedemption([]); // Set empty array on failure
        }
      } else {
        console.warn("No endpoint available for fetching redemptions");
        setRedemption([]);
      }
    } catch (error) {
      console.error("API error fetching redemptions:", error);
      setRedemption([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const [refreshKey, setRefreshKey] = useState(0); // Key to force RedemptionTable refresh

  const triggerRedemptionListUpdate = (redemption) => {
    console.log("New/Updated Redemption: ", redemption);
    setShowRedemptionForm(false);

    const currentPortal = sessionStorage.getItem("selectedPortal");
    if (currentPortal === "Administrator") {
      // For admin portal, trigger RedemptionTable refresh by updating key
      setRefreshKey((prev) => prev + 1);
    } else {
      // For merchant portal, fetch redemptions normally
      setLoading(true);
      fetchRedemptions();
    }
  };

  const fieldsToDisplay = [
    { key: "vehicle", label: "Vehicle", className: "font-bold" },
    { key: "product", label: "Product", className: "font-bold" },
    { key: "merchantStore", label: "Merchant Store", className: "font-bold" },
    { key: "merchantUser", label: "Merchant User", className: "font-bold" },
    {
      key: "redemptionDateUTC",
      label: "Redemption Date",
      className: "font-bold"
    }
  ];

  console.log("redemptionItems", redemptionItems);

  return (
    <div className="page redemption w-full flex flex-column items-center p-4">
      {loading && <GlobalLoadingSpinner />}
      <div className="pageTitle">
        <h2>Redemptions</h2>
      </div>
      <div className="flex w-full">
        <div className="page-content min-h-h-screen flex flex-column justify-start w-full">
          <div className="flex flex-column items-end gap-4 items-center">
            {sessionStorage.getItem("selectedPortal") == "Administrator" && (
              <div className="pageActions">
                <Button variant="contained" onClick={() => setShowRedemptionForm(true)}>
                  Add New Redemption
                </Button>
                {showFetchPaidDates && (
                  <Button variant="outlined" sx={{ ml: 2 }} onClick={() => setShowPaidDatesDialog(true)}>
                    Fetch paid dates
                  </Button>
                )}
                <Dialog open={showPaidDatesDialog} onClose={closePaidDatesDialog} fullWidth maxWidth="xs">
                  <DialogTitle>Fetch paid dates from Xero</DialogTitle>
                  <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                      <TextField
                        label="Redemptions from"
                        type="date"
                        size="small"
                        value={paidDatesFrom}
                        onChange={(e) => setPaidDatesFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        helperText="Leave blank for all dates. Oldest bills are fetched first."
                        disabled={fetchingPaidDates}
                      />
                      <TextField
                        label="Bills to fetch"
                        type="number"
                        size="small"
                        value={paidDatesLimit}
                        onChange={(e) => setPaidDatesLimit(e.target.value)}
                        inputProps={{ min: 1, max: PAID_DATE_FETCH_MAX_INVOICES, step: 1 }}
                        helperText={`1 to ${PAID_DATE_FETCH_MAX_INVOICES}. Bills that already have a paid date are skipped.`}
                        disabled={fetchingPaidDates}
                      />
                      {paidDatesResult && (
                        <Alert severity={paidDatesResult.severity}>{paidDatesResult.message}</Alert>
                      )}
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={closePaidDatesDialog} disabled={fetchingPaidDates}>
                      Close
                    </Button>
                    <Button variant="contained" onClick={fetchPaidDates} disabled={fetchingPaidDates}>
                      {fetchingPaidDates ? "Fetching…" : "Fetch"}
                    </Button>
                  </DialogActions>
                </Dialog>
                <ModalPopup
                  className="w-full"
                  showModal={showRedemptionForm}
                  handleShowModal={() => setShowRedemptionForm(false)}
                  popupTitle="Add New Redemption"
                  popupContent={
                    <RedemptionForm
                      callback={triggerRedemptionListUpdate}
                      closeModal={() => setShowRedemptionForm(false)}
                    />
                  }
                />
              </div>
            )}
            {sessionStorage.getItem("selectedPortal") == "Merchant" && (
              <div className="pageActions">
                <Button variant="contained" onClick={() => setShowRedemptionScanner(true)}>
                  Redeem Redemption
                </Button>
                <ModalPopup
                  className="w-full"
                  showModal={showRedemptionScanner}
                  handleShowModal={() => setShowRedemptionScanner(false)}
                  popupTitle="Redeem Redemption"
                  popupContent={
                    <RedemptionScanner
                      callback={triggerRedemptionListUpdate}
                      closeModal={() => setShowRedemptionScanner(false)}
                    />
                  }
                />
              </div>
            )}
            <RedemptionTable
              key={refreshKey} // Force re-render when refreshKey changes
              redemptions={redemptionItems}
              onRedemptionsUpdate={triggerRedemptionListUpdate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Redemption;
